import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { APIError, api } from '@/lib/api';
import { AuthInput } from './AuthInput';
import { AuthShell } from './AuthShell';
import { validateEmail, validatePassword } from './validation';

const MIN_PASSWORD = 8;

interface Errors {
  email?: string | undefined;
  code?: string | undefined;
  password?: string | undefined;
  confirm?: string | undefined;
}

export function SignupPage() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Errors>({});

  // Verification state.
  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyToken, setVerifyToken] = useState('');

  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function setError(key: keyof Errors, msg?: string) {
    setErrors((e) => ({ ...e, [key]: msg }));
  }

  // Editing the email invalidates any prior verification.
  function onEmailChange(v: string) {
    setEmail(v);
    setError('email', undefined);
    if (codeSent || emailVerified) {
      setCodeSent(false);
      setEmailVerified(false);
      setVerifyToken('');
      setCode('');
    }
  }

  async function sendCode() {
    const emailError = validateEmail(email);
    if (emailError) {
      setError('email', emailError);
      return;
    }
    setSendingCode(true);
    try {
      const res = await api.auth.sendCode(email.trim());
      setCodeSent(true);
      toast.success(res.message);
    } catch (err) {
      const msg = err instanceof APIError ? err.detail : 'Could not send code';
      if (err instanceof APIError && err.status === 409) setError('email', msg);
      toast.error(msg);
    } finally {
      setSendingCode(false);
    }
  }

  async function verifyCode() {
    if (code.trim().length < 6) {
      setError('code', 'Enter the 6-digit code.');
      return;
    }
    setVerifyingCode(true);
    try {
      const res = await api.auth.verifyCode({ email: email.trim(), code: code.trim() });
      setVerifyToken(res.email_verification_token);
      setEmailVerified(true);
      setError('code', undefined);
      toast.success('Email verified ✓');
    } catch (err) {
      setError('code', err instanceof APIError ? err.detail : 'Invalid code');
    } finally {
      setVerifyingCode(false);
    }
  }

  function validateForSubmit(): boolean {
    const next: Errors = {
      password: validatePassword(password, { min: MIN_PASSWORD }),
      confirm: confirm !== password ? 'Passwords do not match.' : undefined,
    };
    setErrors((e) => ({ ...e, ...next }));
    return !next.password && !next.confirm;
  }

  async function submit() {
    if (!emailVerified) {
      toast.error('Please verify your email first.');
      return;
    }
    if (!validateForSubmit()) return;
    setSubmitting(true);
    try {
      await api.auth.signup({
        email: email.trim(),
        password,
        email_verification_token: verifyToken,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      toast.success('Account created — please log in.');
      await navigate({ to: '/login' });
    } catch (err) {
      toast.error(err instanceof APIError ? err.detail : 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell title="Create your account" subtitle="Verify your email, then set a password">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <AuthInput
            id="first_name"
            label="First name"
            value={firstName}
            onChange={setFirstName}
            autoComplete="given-name"
            placeholder="Optional"
          />
          <AuthInput
            id="last_name"
            label="Last name"
            value={lastName}
            onChange={setLastName}
            autoComplete="family-name"
            placeholder="Optional"
          />
        </div>

        {/* Email + verify */}
        <div className="space-y-1.5">
          <label htmlFor="email" className="block text-xs font-medium text-foreground">
            Email<span className="ml-0.5 text-destructive">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <AuthInput
                id="email"
                label=""
                type="email"
                value={email}
                onChange={onEmailChange}
                autoComplete="email"
                placeholder="you@example.com"
                disabled={emailVerified}
                error={errors.email}
                adornment={
                  emailVerified ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Verified" />
                  ) : undefined
                }
              />
            </div>
            {!emailVerified && (
              <button
                type="button"
                onClick={() => void sendCode()}
                disabled={sendingCode || !email}
                className="h-10 shrink-0 rounded-lg border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
              >
                {sendingCode ? 'Sending…' : codeSent ? 'Resend' : 'Verify'}
              </button>
            )}
          </div>
          {emailVerified && (
            <p className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle2 className="h-3.5 w-3.5" /> Email verified
            </p>
          )}
        </div>

        {/* Code entry — appears after a code is sent, until verified */}
        {codeSent && !emailVerified && (
          <div className="space-y-1.5 rounded-lg border border-border bg-muted/40 p-3">
            <p className="text-xs text-muted-foreground">
              Enter the 6-digit code we sent to{' '}
              <span className="font-medium text-foreground">{email.trim()}</span>.
            </p>
            <div className="flex gap-2">
              <div className="flex-1">
                <AuthInput
                  id="code"
                  label=""
                  value={code}
                  onChange={(v) => {
                    setCode(v.replace(/\D/g, '').slice(0, 6));
                    setError('code', undefined);
                  }}
                  autoComplete="one-time-code"
                  placeholder="123456"
                  error={errors.code}
                />
              </div>
              <button
                type="button"
                onClick={() => void verifyCode()}
                disabled={verifyingCode || code.length < 6}
                className="h-10 shrink-0 rounded-lg bg-foreground px-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {verifyingCode ? 'Checking…' : 'Confirm'}
              </button>
            </div>
          </div>
        )}

        <AuthInput
          id="password"
          label={`Password (min ${MIN_PASSWORD} characters)`}
          type="password"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setError('password', undefined);
          }}
          required
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.password}
        />
        <AuthInput
          id="confirm"
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v);
            setError('confirm', undefined);
          }}
          required
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirm}
        />

        <button
          type="submit"
          disabled={submitting || !emailVerified}
          title={!emailVerified ? 'Verify your email to continue' : undefined}
          className="mt-1 inline-flex h-10 w-full items-center justify-center rounded-lg bg-foreground px-3 text-sm font-medium text-background shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {submitting ? 'Please wait…' : 'Create account'}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-foreground hover:underline">
          Log in
        </Link>
      </div>
    </AuthShell>
  );
}
